// =============================================================================
// PEDIGREE NODE SERVICE
// =============================================================================

import Cookie from 'js-cookie';
import { CookieKeys, oneClickAddActionTypes as actions } from '../pedigree-constants';

import familyApi from '../../../api/family-api';
import helper_family_api from '../../../helpers/helper-family-api';

import * as model from "../../../model-states/m-family-tree";

import helper from '../../../helpers/index'

import { cloneDeep, isEmpty } from "lodash";

// NOTE:
// These services are mainly for adding and deleting people from the pedigree

export class PedigreeNodeService {
    constructor(pedigreeWrapper) {
        this.pedigreeWrapper = pedigreeWrapper
    }
    /**
     * Add parents to a person
     */
    async addParents(profile) {
        try {
            this.pedigreeWrapper.setState((prevState) => ({
                add_parents_calls_pending: prevState.add_parents_calls_pending + 1,
            }));

            // Save to API
            let should_send_notification = profile.is_blood_related_to_proband;
            const parents = await helper_family_api.create_parents(this.pedigreeWrapper.props.getPedigreeData().getProband().id + "", profile.id, should_send_notification)

            //Id
            let mother = parents.mother;
            let father = parents.father;
            let relationship = parents.relationship;
            mother.rkey = `apimem-${mother.id}`;
            father.rkey = `apimem-${father.id}`;

            // set extra attributes expected to be in relationship data
            relationship.father_id_id = relationship.father_id;
            relationship.mother_id_id = relationship.mother_id;
            relationship.rkey = `r-${relationship.id}`;

            // set extra attributes expected to be in member profile
            mother.diseases = [];
            mother.genetic_testing = [];
            mother.gene_panels = [];
            mother.family_id_id = mother.family_id;
            mother.mother_id = null;
            mother.father_id = null;
            mother.partners = [father];
            mother.relationship_ids = [relationship];
            mother.twin_id_id = mother.twin_id;
            mother.relationship_data = relationship;

            // set extra attributes expected to be in member profile
            father.diseases = [];
            father.genetic_testing = [];
            father.gene_panels = [];
            father.family_id_id = father.family_id;
            father.mother_id = null;
            father.father_id = null;
            father.partners = [mother];
            father.relationship_ids = [relationship];
            father.twin_id_id = father.twin_id;

            this.pedigreeWrapper.props.getPedigreeData().setProfile(father.rkey, father);
            this.pedigreeWrapper.props.getPedigreeData().setProfile(mother.rkey, mother);

            const p = this.pedigreeWrapper.props.getPedigreeData().getProfile(profile.rkey);
            p.father_id = father.id;
            p.father_id_id = father.id;
            p.mother_id = mother.id;
            p.mother_id_id = mother.id;
            this.pedigreeWrapper.props.getPedigreeData().setProfile(p.rkey, p);

        } catch (error) {
            console.error('Error adding parents:', error);
        }
    }

    /**
     * Add children to a person
     */
    async addChildren(profile, type, num_to_add) {
        try {
            this.pedigreeWrapper.setState((prevState) => ({
                add_children_calls_pending: prevState.add_children_calls_pending + 1,
            }));

            // if adding no_children or infertility nodes to an existing couple, update the
            // actual selected profile's infertile or no_children property to true to render
            // fake child, then return so it would not be added to the actual database
            let people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles())

            if (type === 'no_children' || type === 'infertility') {
                let updated_profile = people.find(person => person.id + '' === profile.id + '')
                if (updated_profile.infertile || updated_profile.no_children) {
                    throw new Error("Cannot add children if parents are infertile or marked no children.");
                }
                await this.pedigreeWrapper.updateInfertileNoChildrenValue(profile, type, true)
                this.pedigreeWrapper.setState((prevState) => ({
                    add_children_calls_pending: prevState.add_children_calls_pending - 1,
                }));
                return;
            }

            let partner = profile;
            let spouse = await PedigreeNodeService.getSpouse(profile);

            if (!spouse || !partner) {
                this.pedigreeWrapper.setState((prevState) => ({
                    add_children_calls_pending: prevState.add_children_calls_pending - 1,
                }));
                return;
            }

            let has_children = false;

            let children = people.filter(person => (person.mother_id + '' === partner.id + '' && person.father_id + '' === spouse.id + '') || (person.mother_id + '' === spouse.id + '' && person.father_id + '' === partner.id + ''))

            if (children.find(child => child.is_infertility_node || child.is_no_children_node)) throw new Error("Cannot add children if parents are infertile or marked no children.");

            has_children = (children.length > 0) ? true : false;

            let calls = [];
            let gender = PedigreeNodeService.getChildGender(type);
            let child = model.createSonDaughter(gender);
            child.pregnancy = false;
            if ([actions.MALE_FETAS, actions.FEMALE_FETAS, actions.PREGNANCY_UNKNOWN].includes(type)) {
                child.pregnancy = true;
            }

            child.value = type;

            let should_send_notification = spouse.is_blood_related_to_proband || partner.is_blood_related_to_proband;

            for (let i = 0; i < num_to_add; i++) {
                calls.push(PedigreeNodeService.createMemberAsChild(spouse, partner, child, profile.side, should_send_notification));
            }
            // parallel calls to create children
            let newly_added_children = await Promise.all(calls);

            let showCollapseUnaffected = Cookie.get(CookieKeys.PEDIGREE_COLLAPSE_UNAFFECTED) === "true";

            for (let child_data of newly_added_children) {
                // set extra attributes expected to be in member profile
                child_data.rkey = `apimem-${child_data.id}`;
                child_data.diseases = [];
                child_data.genetic_testing = [];
                child_data.gene_panels = [];
                child_data.family_id_id = child_data.family_id;
                child_data.partners = [];
                child_data.relationship_ids = [];
                child_data.twin_id_id = child_data.twin_id;

                // if newly added single child, then don't collapse
                if (showCollapseUnaffected) {
                    if (num_to_add == 1) {
                        child_data.dont_collapse = true;
                        this.pedigreeWrapper.setState({ showReCollapseButton: true })
                    }
                }


                this.pedigreeWrapper.props.getPedigreeData().setProfile(child_data.rkey, child_data);

                people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles());
            }

            // update mother and father of siblings
            let siblings = people.filter(person => (person.mother_id + '' === newly_added_children[0].mother_id + '' && person.father_id + '' === newly_added_children[0].father_id + '') || (person.mother_id + '' === newly_added_children[0].father_id + '' && person.father_id + '' === newly_added_children[0].mother_id + ''))
            for (let sibling of siblings) {
                sibling.mother_id = newly_added_children[0].mother_id;
                sibling.father_id = newly_added_children[0].father_id;

                this.pedigreeWrapper.props.getPedigreeData().setProfile(sibling.rkey, sibling);
            }

            if (this.pedigreeWrapper.state.add_partner_calls_pending > 0) {
                while (this.pedigreeWrapper.state.add_partner_calls_pending > 0) {
                    await helper.delay(0.5);
                }
            }

        } catch (error) {
            console.error('Error adding children:', error);
        }
    }

    /**
     * Add partner to a profile
     */
    async addPartner(profile, type, hide_after_adding) {
        try {
            this.pedigreeWrapper.setState((prevState) => ({
                add_partner_calls_pending: prevState.add_partner_calls_pending + 1,
            }));

            let partner = model.createPartner();
            let parent_side = profile.side;

            partner.relationship_data.is_parent_blood_related =
                type === "consanguineous";
            partner.relationship_data.marital_status =
                type === "consanguineous" ? "other" : type;

            let should_send_notification = profile.is_blood_related_to_proband;

            // await helper.delay(0.3)

            let partner_data = await helper_family_api.create_partner(
                profile,
                partner,
                parent_side,
                type === "same_sex",
                should_send_notification
            );
            partner_data.sons_count = 0;
            partner_data.daughters_count = 0;

            // set extra attributes expected to be in member profile
            partner_data.relationship_data.rkey = `r-${partner_data.relationship_data.id}`;
            partner_data.relationship_data.mother_id_id = partner_data.relationship_data.mother_id;
            partner_data.relationship_data.father_id_id = partner_data.relationship_data.father_id;
            partner_data.rkey = `apimem-${partner_data.id}`;
            partner_data.diseases = [];
            partner_data.genetic_testing = [];
            partner_data.gene_panels = [];
            partner_data.family_id_id = partner_data.family_id;
            partner_data.mother_id = null;
            partner_data.father_id = null;
            partner_data.mother_id_id = null;
            partner_data.father_id_id = null;
            partner_data.partners = [profile];
            partner_data.relationship_ids = [partner_data.relationship_data];
            partner_data.twin_id_id = partner_data.twin_id;

            if (hide_after_adding) {
                partner_data.hidden = true;
            }
            else {
                partner_data.hidden = false;
            }

            this.pedigreeWrapper.props.getPedigreeData().setProfile(partner_data.rkey, partner_data);

            const p = this.pedigreeWrapper.props.getPedigreeData().getProfile(profile.rkey);
            if (p.relationship_ids) {
                p.relationship_ids.push(partner_data.relationship_data);
            } else {
                p.relationship_ids = [partner_data.relationship_data];
            }

            if (p.partners) {
                p.partners.push(partner_data);
            } else {
                p.partners = [partner_data];
            }
            this.pedigreeWrapper.props.getPedigreeData().setProfile(p.rkey, p);

            return partner_data;

        } catch (error) {
            console.error('Error adding partner:', error);
            return null;
        }
    }

    /**
     * Add siblings to a profile
     */
    async addSiblings(profile, type, num_to_add) {
        try {
            this.pedigreeWrapper.setState((prevState) => ({
                add_siblings_calls_pending: prevState.add_siblings_calls_pending + 1,
            }));

            // start react-flow update code
            let nodes = cloneDeep(this.pedigreeWrapper.reactFlowInstance.getNodes()).concat(cloneDeep(this.pedigreeWrapper.reactFlowInstance.getEdges()))
            const profile_node = nodes.find((n) => n.id + "" === profile.id + "");

            let calls = [];
            let parents = await familyApi.get_member_memberid_parents(profile.id)
            let father = {
                family_id: profile.family_id,
                level: parents.father.level,
                id: parents.father.id,
                gender: "m"
            };
            let mother = { id: parents.mother.id };
            let gender = PedigreeNodeService.getSiblingGender(type);
            var child = model.createSonDaughter(gender);

            let should_send_notification = profile.is_blood_related_to_proband;

            for (let i = 0; i < num_to_add; i++) {
                calls.push(PedigreeNodeService.createMemberAsChild(father, mother, child, profile.side, should_send_notification));
            }

            // parallel calls to create children
            let newly_added_children = await Promise.all(calls);

            let people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles())

            let showCollapseUnaffected = Cookie.get(CookieKeys.PEDIGREE_COLLAPSE_UNAFFECTED) === "true";

            for (let child_data of newly_added_children) {
                child_data.rkey = `apimem-${child_data.id}`;
                child_data.diseases = [];
                child_data.genetic_testing = [];
                child_data.gene_panels = [];
                child_data.family_id_id = child_data.family_id;
                child_data.partners = [];
                child_data.relationship_ids = [];
                child_data.twin_id_id = child_data.twin_id;

                // if newly added single child, then don't collapse
                if (showCollapseUnaffected) {
                    if (num_to_add == 1) {
                        child_data.dont_collapse = true;
                        this.pedigreeWrapper.setState({ showReCollapseButton: true })
                    }
                }

                const p = this.pedigreeWrapper.props.getPedigreeData().getProfile(profile.rkey);

                // Save if there is twins
                if (type.includes("twin")) {
                    const data = await this.pedigreeWrapper.addTwinsEntry(profile.twin_type ? profile.twin_type : "fraternal", profile, child_data);
                    if (data) {
                        // let twin_type = null;
                        const member_pair = data.members;

                        if (member_pair[0].id + "" === p.id + "") {
                            // twin_type = member_pair[0].twin_type;
                            p.twin_id = member_pair[0].twin_id;
                            p.twin_id_id = member_pair[0].twin_id;
                            p.twin_type = member_pair[0].twin_type;
                            p.twin_set = member_pair[0].twin_set;
                        } else if (member_pair[0].id + "" === child_data.id + "") {
                            // twin_type = member_pair[0].twin_type;
                            child_data.twin_id = member_pair[0].twin_id;
                            child_data.twin_id_id = member_pair[0].twin_id;
                            child_data.twin_type = member_pair[0].twin_type;
                            child_data.twin_set = member_pair[0].twin_set;
                        }

                        if (member_pair[1].id + "" === p.id + "") {
                            // twin_type = member_pair[1].twin_type;
                            p.twin_id = member_pair[1].twin_id;
                            p.twin_id_id = member_pair[1].twin_id;
                            p.twin_type = member_pair[1].twin_type;
                            p.twin_set = member_pair[1].twin_set;
                        } else if (member_pair[1].id + "" === child_data.id + "") {
                            // twin_type = member_pair[1].twin_type;
                            child_data.twin_id = member_pair[1].twin_id;
                            child_data.twin_id_id = member_pair[1].twin_id;
                            child_data.twin_type = member_pair[1].twin_type;
                            child_data.twin_set = member_pair[1].twin_set;
                        }
                    }
                }

                this.pedigreeWrapper.props.getPedigreeData().setProfile(p.rkey, p);
                this.pedigreeWrapper.props.getPedigreeData().setProfile(child_data.rkey, child_data);

                people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles());
            }

            // update mother and father of siblings
            let siblings = people.filter(person => (person.mother_id + '' === newly_added_children[0].mother_id + '' && person.father_id + '' === newly_added_children[0].father_id + '') || (person.mother_id + '' === newly_added_children[0].father_id + '' && person.father_id + '' === newly_added_children[0].mother_id + ''))
            for (let sibling of siblings) {
                sibling.mother_id = newly_added_children[0].mother_id;
                sibling.father_id = newly_added_children[0].father_id;

                this.pedigreeWrapper.props.getPedigreeData().setProfile(sibling.rkey, sibling);
            }
        } catch (error) {
            console.error('Error adding siblings:', error);
            return { success: false, error };
        }
    }

    async handleCheckBulkDeleteSelectedPeople()  {
        let selected_ids = this.pedigreeWrapper.props.getPedigreeDrawingData().getSelectedList();
        let member_ids = selected_ids.filter(id => /^\d+$/.test(id));
        let fake_ids = selected_ids.filter(id => /^fake/.test(id));
        if (!(member_ids || fake_ids) || (member_ids.length === 0 && fake_ids.length === 0)) {
          return { msg: null, canDelete: false };
        }

        for (let id of fake_ids) {
          //handle checking of fake no children and infertility nodes if can delete
          const profile = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${id}`);
          if ((profile.is_no_children_node || profile.is_infertility_node) && profile.is_fake_node) {
            let mother = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.mother_id}`)
            let father = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.father_id}`)
            if (!(mother.infertile || mother.no_children || father.infertile || father.no_children)) {
              return { msg: "Cannot delete infertile/no children node if father or mother is not marked as infertile/no children", canDelete: false };
            }
          }
        }

        try {
          const results = [];
          const allDeletableIds = new Set();
          let warningMsg = null;

          if (member_ids.length > 1) {
            warningMsg = "Are you sure you would like to delete the selected family members?";
          }

          const members_deletable_map = {};

          for (let id of member_ids) {
            const deletableMap = await familyApi.can_delete_member(id);
            members_deletable_map[id] = deletableMap;

            if (deletableMap.member_ids.length === 0 && deletableMap.message !== "") {
              return { msg: deletableMap.message, canDelete: false };
            }

            deletableMap.member_ids.forEach(id => allDeletableIds.add(id));

            if (deletableMap.member_ids.length > 1) {
              warningMsg = "One or more selected members have partners/children/donors. Delete all related records?";
            }

            results.push(deletableMap);
          }

          this.pedigreeWrapper.setState({ members_deletable_map });

          return { msg: warningMsg, canDelete: true, deletableIds: Array.from(allDeletableIds) };
        } catch (error) {
          console.error(error);
          return { msg: error.message, canDelete: false };
        }
    };

    pruneDeleteMap(members_delete_map) {
        const pruned = { ...members_delete_map };

        for (const [key, { member_ids }] of Object.entries(members_delete_map)) {
          const memberIds = member_ids.map(String);

          for (const [otherKey, { member_ids: otherIds }] of Object.entries(members_delete_map)) {
            if (key === otherKey) continue;

            const otherMemberIds = otherIds.map(String);

            if (otherMemberIds.includes(String(key))) {
              delete pruned[key];
              break;
            }
          }
        }

        return pruned;
    }

    async handleBulkDeleteSelectedPeople() {
        const selected_ids = this.pedigreeWrapper.props.getPedigreeDrawingData().getSelectedList();
        let member_ids = selected_ids.filter(id => /^\d+$/.test(id));
        let fake_ids = selected_ids.filter(id => /^fake/.test(id));
        if (!(member_ids || fake_ids) || (member_ids.length === 0 && fake_ids.length === 0)) return;

        const members_delete_map = {}

        for (let id of fake_ids) {
          //handle deleting of fake nodes
          const profile = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${id}`);
          if ((profile.is_no_children_node || profile.is_infertility_node) && profile.is_fake_node) {
            let mother = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.mother_id}`)
            let father = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.father_id}`)
            let people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles());
            // delete the fake child node, but not the partner that is not a fake node,
            // and is a record on the database
            if ((mother.infertile || mother.no_children) && !mother.is_fake_node) {
              this.pedigreeWrapper.deleteFromPedigreeData(profile, []);
              // make sure to delete all is_no_children_nodes and is_infertility_nodes that has that mother too
              let peopleToDelete = people.filter(person => (person.is_no_children_node || person.is_infertility_node) && (person.mother_id + '' === mother.id + '' || person.father_id + '' === mother.id + ''))
              for (let p of peopleToDelete) {
                this.pedigreeWrapper.deleteFromPedigreeData(p, []);
              }
              await this.pedigreeWrapper.updateInfertileNoChildrenValue(mother, mother.infertile ? 'infertility' : 'no_children', false)
            }
            else if ((father.infertile || father.no_children) && !father.is_fake_node) {
              this.pedigreeWrapper.deleteFromPedigreeData(profile, []);
              // make sure to delete all is_no_children_nodes and is_infertility_nodes that has that father too
              let peopleToDelete = people.filter(person => (person.is_no_children_node || person.is_infertility_node) && (person.father_id + '' === father.id + '' || person.mother_id + '' === father.id + ''))
              for (let p of peopleToDelete) {
                this.pedigreeWrapper.deleteFromPedigreeData(p, []);
              }
              await this.pedigreeWrapper.updateInfertileNoChildrenValue(father, father.infertile ? 'infertility' : 'no_children', false)
            }

            // when deleting a fake child node, also
            // remove the mother from the pedigree data store if mother is fake node
            if (mother.is_fake_node) {
              this.pedigreeWrapper.deleteFromPedigreeData(mother, [father]);
              this.pedigreeWrapper.deleteFakePartnerAndChild(father)
            }

            // remove the father from the pedigree data store if father is fake node
            if (father.is_fake_node) {
              this.pedigreeWrapper.deleteFromPedigreeData(father, [mother]);
              this.pedigreeWrapper.deleteFakePartnerAndChild(mother)
            }
          }
        }

        for (let id of member_ids) {
          const deletableMap = this.pedigreeWrapper.state.members_deletable_map && id in this.pedigreeWrapper.state.members_deletable_map ? this.pedigreeWrapper.state.members_deletable_map[id] : await familyApi.can_delete_member(id);

          if (!deletableMap.member_ids || deletableMap.member_ids.length === 0) {
            continue;
          }

          members_delete_map[id] = deletableMap;
        }

        let pruned_delete_map = this.pruneDeleteMap(members_delete_map);

        try {
          for (let key in pruned_delete_map) {
            let deletable_members_map = pruned_delete_map[key];

            const deletable_member_ids_map = {}
            for (let z = 0; z < deletable_members_map.member_ids.length; z++) {
              deletable_member_ids_map[deletable_members_map.member_ids[z]] = deletable_members_map.member_ids[z];
            }

            const profile = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${key}`);
            if (!profile) {
              console.log(`Profile not found for key: apimem-${key}`);
              continue;
            }

            const partners = await familyApi.get_member_memberid_partners(profile.id);

            let partner_is_only_one_and_partner_is_fake_node = false;

            if (partners.length === 1) {
              let people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles());
              let one_partner = people.find(person => person.id + '' === partners[0].id + '')
              if (one_partner.is_fake_node) {
                partner_is_only_one_and_partner_is_fake_node = true;
              }
            }

            if ((profile.infertile || profile.no_children) && partner_is_only_one_and_partner_is_fake_node) {
              await this.pedigreeWrapper.deleteFakePartnerAndChild(profile);
            }

            // loop through and see if any of the members to be deleted are marked as no_children or infertile and delete the fake nodes
            for (let z = 0; z < deletable_members_map.member_ids.length; z++) {
              const people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles());
              const marked_for_deletion = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${deletable_members_map.member_ids[z]}`);
              if ((marked_for_deletion.infertile || marked_for_deletion.no_children) && !marked_for_deletion.is_fake_node) {
                let peopleToDelete = people.filter((person) => {
                  return (person.is_no_children_node || person.is_infertility_node) &&
                    (person.mother_id + '' === marked_for_deletion.id + '' || person.father_id + '' === marked_for_deletion.id + '')
                });
                for (let p of peopleToDelete) {
                  this.pedigreeWrapper.deleteFromPedigreeData(p, []);
                }
              }
            }

            // loop through and get any children of the members to be deleted and set their mother_id and father_id to null
            let temp_profiles = cloneDeep(Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles()));
            for (let z = 0; z < temp_profiles.length; z++) {
              let temp_profile = temp_profiles[z];
              if (temp_profile.mother_id && temp_profile.father_id) {
                if ((temp_profile.mother_id in deletable_member_ids_map) ||
                  (temp_profile.father_id in deletable_member_ids_map)) {
                  temp_profile.mother_id = null;
                  temp_profile.mother_id_id = null;
                  temp_profile.father_id = null;
                  temp_profile.father_id_id = null;
                  this.pedigreeWrapper.props.getPedigreeData().setProfile(`apimem-${temp_profile.id}`, temp_profile);
                }
              }
            }

            // loop through and remove the person from their partners partner array and relationship_ids array
            // relationship_data should also be reset if it matches the id
            temp_profiles = cloneDeep(Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles()));
            for (let z = 0; z < temp_profiles.length; z++) {
              let changed = false;
              let temp_profile = temp_profiles[z];
              let partners = temp_profile.partners;
              let relationship_ids = temp_profile.relationship_ids;

              if (Array.isArray(partners)) {
                const new_partners = [];
                for (let l = 0; l < partners.length; l++) {
                  if (partners[l].id in deletable_member_ids_map) {
                    continue;
                  } else {
                    new_partners.push(partners[l]);
                  }
                }

                if (new_partners.length !== partners.length) {
                  temp_profile.partners = new_partners;
                  changed = true;
                }
              }

              if (Array.isArray(relationship_ids)) {
                const new_relationships = [];
                for (let l = 0; l < relationship_ids.length; l++) {
                  if (relationship_ids[l].mother_id in deletable_member_ids_map || relationship_ids[l].father_id in deletable_member_ids_map) {
                    continue;
                  } else {
                    new_relationships.push(relationship_ids[l]);
                  }
                }

                if (new_relationships.length !== relationship_ids.length) {
                  temp_profile.relationship_ids = new_relationships;
                  changed = true;
                }
              }

              if (temp_profile.relationship_data &&
                (temp_profile.relationship_data.mother_id in deletable_member_ids_map || temp_profile.relationship_data.father_id in deletable_member_ids_map)) {
                delete temp_profile.relationship_data;
              }

              if (changed) {
                this.pedigreeWrapper.props.getPedigreeData().setProfile(`apimem-${temp_profile.id}`, temp_profile);
              }
            }

            // delete all member ids from central partners array
            for (let z = 0; z < deletable_members_map.member_ids.length; z++) {
              const key = `apimem-${deletable_members_map.member_ids[z]}`;
              if (key in this.pedigreeWrapper.props.getPedigreeData().getAllPartners()) {
                this.pedigreeWrapper.props.getPedigreeData().deletePartnersByKey(key);
              }
            }

            // delete all member ids from data
            for (let z = 0; z < deletable_members_map.member_ids.length; z++) {
              let p = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${deletable_members_map.member_ids[z]}`);
              this.pedigreeWrapper.deleteFromPedigreeData(p, []);
            }

            // hit bulk delete endpoint
            await familyApi.bulk_delete_members(profile.id, deletable_members_map.member_ids);

            if (profile.twin_id_id) {
              let people = cloneDeep(Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles()));
              const twins = [];
              for (let i = 0; i < people.length; i++) {
                if (people[i].twin_id_id === profile.twin_id_id) twins.push(people[i]);
              }

              if (twins.length === 1) {
                // delete the twin entry and clear twin_id and twin_set
                // if there is only one twin left after deletion

                /* Note: deletion of twin record is done within member deletion if
                ** only one twin is left, no need to manually do it
                */
                // await this.deleteTwinsEntry(profile);

                twins[0].twin_id_id = null;
                twins[0].twin_id = null;
                twins[0].twin_set = null;
                twins[0].twin_type = null;
                this.pedigreeWrapper.props.getPedigreeData().setProfile(twins[0].rkey, twins[0]);
              }
            }
          }

          this.pedigreeWrapper.setState({
            openModalConfirmDelete: false,
            errorMessages: [],
            members_deletable_map: {}
          });

          await this.pedigreeWrapper.addFakePartnersAndChildren();
          await this.pedigreeWrapper.reRenderPedigree();

      } catch (err) {
        console.error("Bulk delete error:", err);
      }
    };

    handleNodeDelete() {
        let selected_ids = this.pedigreeWrapper.props.getPedigreeDrawingData().getSelectedList()
        if (selected_ids.length > 1) {
          this.handleBulkDeleteSelectedPeople();
        } else {
          this.handleDeleteSelectedPerson();
        }
    };

    async handleCheckDeleteSelectedPerson() {

        const node = this.pedigreeWrapper.state.selectedNode;
        if (node) {
            const profile = node.data.profile;
            //handle checking of fake no children and infertility nodes if can delete
            if ((profile.is_no_children_node || profile.is_infertility_node) && profile.is_fake_node) {
                let mother = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.mother_id}`)
                let father = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.father_id}`)
                if (mother.infertile || mother.no_children || father.infertile || father.no_children) {
                    return { msg: null, canDelete: true };
                }
            }

            try {

                const deletable_members_map = await familyApi.can_delete_member(profile.id);
                if (deletable_members_map.member_ids.length === 0 && deletable_members_map.message !== "") {
                    const error = {
                        message: deletable_members_map.message,
                        type: ""
                    };
                    this.pedigreeWrapper.setState({ errorMessages: [error.message] });
                    return { msg: null, canDelete: false };
                }

                if (deletable_members_map.member_ids.length === 2) {
                    // hack to check if the two members are ancestors of proband (not full proof in the future)
                    let member_one = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${deletable_members_map.member_ids[0]}`);
                    let member_two = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${deletable_members_map.member_ids[1]}`);

                    if (!(member_one.father_id) && !(member_one.mother_id) && !(member_two.father_id) && !(member_two.mother_id)) {
                        return { msg: null, canDelete: true };
                    } else {
                        return { msg: "This family member has partners and/or children and/or donors. Do you want to delete this family member, their partners, children, and donors?", canDelete: true };
                    }

                } else if (deletable_members_map.member_ids.length > 1) {
                    return { msg: "This family member has partners and/or children and/or donors. Do you want to delete this family member, their partners, children, and donors?", canDelete: true };
                } else {
                    return { msg: null, canDelete: true };
                }

            } catch (error) {
                this.pedigreeWrapper.setState({ errorMessages: [error.message] });
                return { msg: null, canDelete: false };
            }

            // const can_delete = this.validateDeleteOrFail(node);
            // if (can_delete) {
            //   try {
            //     const profile = node.data.profile;
            //     //handle checking of fake no children and infertility nodes if can delete
            //     if ((profile.is_no_children_node || profile.is_infertility_node) && profile.is_fake_node){
            //       let mother = this.props.getPedigreeData().getProfile(`apimem-${profile.mother_id}`)
            //       let father = this.props.getPedigreeData().getProfile(`apimem-${profile.father_id}`)
            //       if(mother.infertile || mother.no_children || father.infertile || father.no_children){
            //         return true;
            //       }
            //     }
            //
            //     // TODO: switch this over to use the central store instead of hitting API
            //
            //     const partners = await familyApi.get_member_memberid_partners(profile.id);
            //     if (Array.isArray(partners) && partners.length > 0) {
            //       const children = await familyApi.get_members_memberid_children(profile.id, partners[0].id);
            //       if (!profile.mother_id && !profile.father_id && profile.is_blood_related_to_proband) {
            //
            //         let consanguineous = profile.partners.length > 0 ? profile.partners[0].is_blood_related_to_proband : false
            //         if(consanguineous && children.length === 0){
            //           return true;
            //         }
            //         // top level member above grandparent
            //         // delete top level parents
            //         if (children.length === 0) {
            //           return false;
            //         }
            //
            //       } else {
            //
            //         //check first for partners before children
            //         if (partners.length > 0 && profile.is_blood_related_to_proband) {
            //           throw new Error('This family member cannot be deleted because they have partner. Please delete the partner first.')
            //         }
            //
            //         if (children.length > 0 || profile.is_blood_related_to_proband) {
            //           throw new Error('This family member cannot be deleted because they have children. Please delete the children first.')
            //         }
            //
            //         // otherwise we assume either there is children or the blood related
            //         // family member still has partners that need to be deleted
            //
            //       }
            //     }
            //     return true;
            //
            //   } catch (error) {
            //     if(error.message == 'Cannot delete because this person has 2 or more children.'){
            //       error.type = 'toplevel'
            //     }
            //     else if(error.message == 'This family member cannot be deleted because they have children. Please delete the children first.'){
            //       error.type = 'children'
            //     }
            //     console.log(error)
            //     this.setState({ errorMessages: [error.message] });
            //     // this.setState({ openCannotDeleteModal: [error.type, true] });
            //     return false;
            //   }
            // }

        }

        return false;
    }

    // Delete selected person
    async handleDeleteSelectedPerson() {

        const node = this.pedigreeWrapper.state.selectedNode;
        if (node) {

            const profile = node.data.profile;
            let saved_data = this.pedigreeWrapper.getActiveSavedNodePositions();

            //handle deleting of fake nodes
            if ((profile.is_no_children_node || profile.is_infertility_node) && profile.is_fake_node) {
                let mother = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.mother_id}`)
                let father = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.father_id}`)
                let people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles());
                // delete the fake child node, but not the partner that is not a fake node,
                // and is a record on the database
                if ((mother.infertile || mother.no_children) && !mother.is_fake_node) {
                    this.pedigreeWrapper.deleteFromPedigreeData(profile, []);
                    // make sure to delete all is_no_children_nodes and is_infertility_nodes that has that mother too
                    let peopleToDelete = people.filter(person => (person.is_no_children_node || person.is_infertility_node) && (person.mother_id + '' === mother.id + '' || person.father_id + '' === mother.id + ''))
                    for (let p of peopleToDelete) {
                        this.pedigreeWrapper.deleteFromPedigreeData(p, []);
                    }
                    await this.pedigreeWrapper.updateInfertileNoChildrenValue(mother, mother.infertile ? 'infertility' : 'no_children', false)
                }
                else if ((father.infertile || father.no_children) && !father.is_fake_node) {
                    this.pedigreeWrapper.deleteFromPedigreeData(profile, []);
                    // make sure to delete all is_no_children_nodes and is_infertility_nodes that has that father too
                    let peopleToDelete = people.filter(person => (person.is_no_children_node || person.is_infertility_node) && (person.father_id + '' === father.id + '' || person.mother_id + '' === father.id + ''))
                    for (let p of peopleToDelete) {
                        this.pedigreeWrapper.deleteFromPedigreeData(p, []);
                    }
                    await this.pedigreeWrapper.updateInfertileNoChildrenValue(father, father.infertile ? 'infertility' : 'no_children', false)
                }

                // when deleting a fake child node, also
                // remove the mother from the pedigree data store if mother is fake node
                if (mother.is_fake_node) {
                    this.pedigreeWrapper.deleteFromPedigreeData(mother, [father]);
                    this.pedigreeWrapper.deleteFakePartnerAndChild(father)
                }

                // remove the father from the pedigree data store if father is fake node
                if (father.is_fake_node) {
                    this.pedigreeWrapper.deleteFromPedigreeData(father, [mother]);
                    this.pedigreeWrapper.deleteFakePartnerAndChild(mother)
                }

                if (this.pedigreeWrapper.state.showSideBar) {
                    this.pedigreeWrapper.handleSideBarClose();
                }

                this.pedigreeWrapper.setState({
                    openModalConfirmDelete: false,
                    errorMessages: []
                });

                await this.pedigreeWrapper.addFakePartnersAndChildren();

                if (!isEmpty(saved_data)) {
                    await this.pedigreeWrapper.reRenderPedigree(true, undefined, undefined, undefined, undefined, true);

                    let currentEls = cloneDeep(this.pedigreeWrapper.reactFlowInstance.getNodes()).concat(cloneDeep(this.pedigreeWrapper.reactFlowInstance.getEdges()))
                    await this.pedigreeWrapper.saveLayoutData(currentEls)
                }
                else {
                    await this.pedigreeWrapper.reRenderPedigree(undefined, undefined, undefined, undefined, true, undefined);
                }

                // const nodes = cloneDeep(this.pedigreeWrapper.reactFlowInstance.getNodes()).concat(cloneDeep(this.pedigreeWrapper.reactFlowInstance.getEdges()));
                // await this.pedigreeWrapper.saveLayoutData(nodes);

                return;
            }

            try {

                const deletable_members_map = await familyApi.can_delete_member(profile.id);
                if (deletable_members_map.member_ids.length === 0 && deletable_members_map.message !== "") {
                    const error = {
                        message: deletable_members_map.message,
                        type: ""
                    };
                    console.log(error)
                    this.pedigreeWrapper.setState({ errorMessages: [error.message] });
                }

                // make a map of the delete ids
                const deletable_member_ids_map = {}
                for (let z = 0; z < deletable_members_map.member_ids.length; z++) {
                    deletable_member_ids_map[deletable_members_map.member_ids[z]] = deletable_members_map.member_ids[z];
                }


                // this is a holdover from the old delete, kept it for now
                const partners = await familyApi.get_member_memberid_partners(profile.id);

                let partner_is_only_one_and_partner_is_fake_node = false;

                if (partners.length === 1) {
                    let people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles());
                    let one_partner = people.find(person => person.id + '' === partners[0].id + '')
                    if (one_partner.is_fake_node) {
                        partner_is_only_one_and_partner_is_fake_node = true;
                    }
                }

                if ((profile.infertile || profile.no_children) && partner_is_only_one_and_partner_is_fake_node) {
                    await this.pedigreeWrapper.deleteFakePartnerAndChild(profile);
                }

                // loop through and see if any of the members to be deleted are marked as no_children or infertile and delete the fake nodes
                for (let z = 0; z < deletable_members_map.member_ids.length; z++) {
                    const people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles());
                    const marked_for_deletion = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${deletable_members_map.member_ids[z]}`);
                    if ((marked_for_deletion.infertile || marked_for_deletion.no_children) && !marked_for_deletion.is_fake_node) {
                        let peopleToDelete = people.filter((person) => {
                            return (person.is_no_children_node || person.is_infertility_node) &&
                                (person.mother_id + '' === marked_for_deletion.id + '' || person.father_id + '' === marked_for_deletion.id + '')
                        });
                        for (let p of peopleToDelete) {
                            this.pedigreeWrapper.deleteFromPedigreeData(p, []);
                        }
                    }
                }

                // loop through and get any children of the memebrs to be deleted and set their mother_id and father_id to null
                let temp_profiles = cloneDeep(Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles()));
                let relationship_lengths_before_delete = {};
                for (let z = 0; z < temp_profiles.length; z++) {
                    let temp_profile = temp_profiles[z];
                    if (temp_profile.mother_id && temp_profile.father_id) {
                        if ((temp_profile.mother_id in deletable_member_ids_map) ||
                            (temp_profile.father_id in deletable_member_ids_map)) {
                            temp_profile.mother_id = null;
                            temp_profile.mother_id_id = null;
                            temp_profile.father_id = null;
                            temp_profile.father_id_id = null;
                            this.pedigreeWrapper.props.getPedigreeData().setProfile(`apimem-${temp_profile.id}`, temp_profile);
                        }
                    }
                }

                // loop through and remove the person from their partners partner array and relationship_ids array
                // relationship_data should also be reset if it matches the id
                temp_profiles = cloneDeep(Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles()));
                for (let z = 0; z < temp_profiles.length; z++) {
                    let changed = false;
                    let temp_profile = temp_profiles[z];
                    let partners = temp_profile.partners;
                    let relationship_ids = temp_profile.relationship_ids;
                    relationship_lengths_before_delete[`apimem-${temp_profile.id}`] = temp_profile.relationship_ids.length;


                    if (Array.isArray(partners)) {
                        const new_partners = [];
                        for (let l = 0; l < partners.length; l++) {
                            if (partners[l].id in deletable_member_ids_map) {
                                continue;
                            } else {
                                new_partners.push(partners[l]);
                            }
                        }

                        if (new_partners.length !== partners.length) {
                            temp_profile.partners = new_partners;
                            changed = true;
                        }
                    }

                    if (Array.isArray(relationship_ids)) {
                        const new_relationships = [];
                        for (let l = 0; l < relationship_ids.length; l++) {
                            if (relationship_ids[l].mother_id in deletable_member_ids_map || relationship_ids[l].father_id in deletable_member_ids_map) {
                                continue;
                            } else {
                                new_relationships.push(relationship_ids[l]);
                            }
                        }

                        if (new_relationships.length !== relationship_ids.length) {
                            temp_profile.relationship_ids = new_relationships;
                            changed = true;
                        }
                    }

                    if (temp_profile.relationship_data &&
                        (temp_profile.relationship_data.mother_id in deletable_member_ids_map || temp_profile.relationship_data.father_id in deletable_member_ids_map)) {
                        delete temp_profile.relationship_data;
                    }

                    if (changed) {
                        this.pedigreeWrapper.props.getPedigreeData().setProfile(`apimem-${temp_profile.id}`, temp_profile);
                    }
                }

                // delete all member ids from central partners array
                for (let z = 0; z < deletable_members_map.member_ids.length; z++) {
                    const key = `apimem-${deletable_members_map.member_ids[z]}`;
                    if (key in this.pedigreeWrapper.props.getPedigreeData().getAllPartners()) {
                        this.pedigreeWrapper.props.getPedigreeData().deletePartnersByKey(key);
                    }
                }

                // delete all member ids from data
                for (let z = 0; z < deletable_members_map.member_ids.length; z++) {
                    let p = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${deletable_members_map.member_ids[z]}`);
                    this.pedigreeWrapper.deleteFromPedigreeData(p, [], relationship_lengths_before_delete[`apimem-${p.id}`]);
                }

                // hit bulk delete endpoint
                await familyApi.bulk_delete_members(profile.id, deletable_members_map.member_ids);

                if (profile.twin_id_id) {
                    let people = cloneDeep(Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles()));
                    const twins = [];
                    for (let i = 0; i < people.length; i++) {
                        if (people[i].twin_id_id === profile.twin_id_id) twins.push(people[i]);
                    }

                    if (twins.length === 1) {
                        // delete the twin entry and clear twin_id and twin_set
                        // if there is only one twin left after deletion

                        /* Note: deletion of twin record is done within member deletion if
                        ** only one twin is left, no need to manually do it
                        */
                        // await this.pedigreeWrapper.deleteTwinsEntry(profile);

                        twins[0].twin_id_id = null;
                        twins[0].twin_id = null;
                        twins[0].twin_set = null;
                        twins[0].twin_type = null;
                        this.pedigreeWrapper.props.getPedigreeData().setProfile(twins[0].rkey, twins[0]);
                    }
                }

                if (this.pedigreeWrapper.state.showSideBar) {
                    this.pedigreeWrapper.handleSideBarClose();
                }

                this.pedigreeWrapper.setState({
                    openModalConfirmDelete: false,
                    errorMessages: []
                });

                await this.pedigreeWrapper.addFakePartnersAndChildren();

                if (!isEmpty(saved_data)) {
                    await this.pedigreeWrapper.reRenderPedigree(true, undefined, undefined, undefined, undefined, true, undefined);

                    let currentEls = cloneDeep(this.pedigreeWrapper.reactFlowInstance.getNodes()).concat(cloneDeep(this.pedigreeWrapper.reactFlowInstance.getEdges()))
                    await this.pedigreeWrapper.saveLayoutData(currentEls)
                }
                else {
                    await this.pedigreeWrapper.reRenderPedigree();
                }


                // const nodes = cloneDeep(this.pedigreeWrapper.reactFlowInstance.getNodes()).concat(cloneDeep(this.pedigreeWrapper.reactFlowInstance.getEdges()));
                // await this.pedigreeWrapper.saveLayoutData(nodes);

            } catch (error) {
                console.log(error);
                this.pedigreeWrapper.setState({ errorMessages: [error.message] });
            }

            //////////////////////////////////////////////////////////////////////////////////////////////

            // const can_delete = this.pedigreeWrapper.validateDeleteOrFail(node);
            // if (can_delete) {
            //   try {
            //     const profile = node.data.profile;
            //
            //     //handle deleting of fake nodes
            //     if ((profile.is_no_children_node || profile.is_infertility_node) && profile.is_fake_node){
            //       let mother = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.mother_id}`)
            //       let father = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${profile.father_id}`)
            //       let people = cloneDeep(Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles()));
            //       // delete the fake child node, but not the partner that is not a fake node,
            //       // and is a record on the database
            //       if((mother.infertile || mother.no_children) && !mother.is_fake_node){
            //         this.pedigreeWrapper.deleteFromPedigreeData(profile, []);
            //         // make sure to delete all is_no_children_nodes and is_infertility_nodes that has that mother too
            //         let peopleToDelete = people.filter(person => (person.is_no_children_node || person.is_infertility_node) && person.mother_id == mother.id || person.father_id == mother.id)
            //         for(let p of peopleToDelete){
            //           this.pedigreeWrapper.deleteFromPedigreeData(p, []);
            //         }
            //         await this.pedigreeWrapper.updateInfertileNoChildrenValue(mother, mother.infertile ? 'infertility' : 'no_children', false)
            //       }
            //       else if((father.infertile || father.no_children) && !father.is_fake_node){
            //         this.pedigreeWrapper.deleteFromPedigreeData(profile, []);
            //         // make sure to delete all is_no_children_nodes and is_infertility_nodes that has that father too
            //         let peopleToDelete = people.filter(person => (person.is_no_children_node || person.is_infertility_node) && person.father_id == father.id || person.mother_id == father.id)
            //         for(let p of peopleToDelete){
            //           this.pedigreeWrapper.deleteFromPedigreeData(p, []);
            //         }
            //         await this.pedigreeWrapper.updateInfertileNoChildrenValue(father, father.infertile ? 'infertility' : 'no_children', false)
            //       }
            //
            //       // when deleting a fake child node, also
            //       // remove the mother from the pedigree data store if mother is fake node
            //       if(mother.is_fake_node){
            //         this.pedigreeWrapper.deleteFromPedigreeData(mother, [father]);
            //         this.pedigreeWrapper.deleteFakePartnerAndChild(father)
            //       }
            //
            //       // remove the father from the pedigree data store if father is fake node
            //       if(father.is_fake_node){
            //         this.pedigreeWrapper.deleteFromPedigreeData(father, [mother]);
            //         this.pedigreeWrapper.deleteFakePartnerAndChild(mother)
            //       }
            //
            //       if (this.pedigreeWrapper.state.showSideBar) {
            //         this.pedigreeWrapper.handleSideBarClose();
            //       }
            //
            //       this.pedigreeWrapper.setState({
            //         openModalConfirmDelete: false,
            //         errorMessages: []
            //       });
            //
            //       await this.pedigreeWrapper.addFakePartnersAndChildren();
            //       await this.pedigreeWrapper.reRenderPedigree();
            //       return;
            //     }
            //
            //     const partners = await familyApi.get_member_memberid_partners(profile.id);
            //
            //     let partner_is_only_one_and_partner_is_fake_node = false;
            //
            //     if(partners.length === 1){
            //       let people = cloneDeep(Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles()));
            //       let one_partner = people.find(person => person.id == partners[0].id)
            //       if(one_partner.is_fake_node){
            //         partner_is_only_one_and_partner_is_fake_node = true;
            //       }
            //     }
            //
            //     if((profile.infertile || profile.no_children) && partner_is_only_one_and_partner_is_fake_node){
            //       await this.pedigreeWrapper.deleteFakePartnerAndChild(profile);
            //     }
            //
            //     if (Array.isArray(partners) && partners.length > 0) {
            //       const children = await familyApi.get_members_memberid_children(profile.id, partners[0].id);
            //       if (!profile.mother_id && !profile.father_id && profile.is_blood_related_to_proband) {
            //
            //         let consanguineous = profile.partners.length > 0 ? profile.partners[0].is_blood_related_to_proband : false
            //         if(consanguineous && children.length === 0){
            //           await familyApi.delete_member_memberid(profile.id);
            //           this.pedigreeWrapper.deleteFromPedigreeData(profile, partners);
            //         }
            //
            //         // top level member above grandparent
            //         // delete top level parents
            //         if (children.length > 0) {
            //           await familyApi.delete_parents_top_level(children[0].id);
            //
            //           // remove father_id and mother_id from children
            //           for (let m=0; m<children.length; m++) {
            //             const child_profile = this.pedigreeWrapper.props.getPedigreeData().getProfile(`apimem-${children[m].id}`);
            //             child_profile.father_id = null;
            //             child_profile.father_id_id = null;
            //             child_profile.mother_id = null;
            //             child_profile.mother_id_id = null;
            //             this.pedigreeWrapper.props.getPedigreeData().setProfile(`apimem-${children[m].id}`, child_profile);
            //           }
            //
            //           // delete the two top level parents from central store
            //           this.pedigreeWrapper.deleteFromPedigreeData(profile, []);
            //
            //           partners[0].rkey = `apimem-${partners[0].id}`;
            //           this.pedigreeWrapper.deleteFromPedigreeData(partners[0], []);
            //         }
            //
            //       } else {
            //
            //         if (children.length === 0 && !profile.is_blood_related_to_proband) {
            //           await familyApi.delete_member_memberid(profile.id);
            //           this.pedigreeWrapper.deleteFromPedigreeData(profile, partners);
            //           await this.pedigreeWrapper.deleteFakePartnerAndChild(profile)
            //           await this.pedigreeWrapper.addFakePartnersAndChildren();
            //         }
            //
            //       }
            //     } else {
            //       await familyApi.delete_member_memberid(profile.id);
            //       this.pedigreeWrapper.deleteFromPedigreeData(profile, partners);
            //     }
            //
            //     if (profile.twin_id_id) {
            //       let people = Object.values(this.pedigreeWrapper.props.getPedigreeData().getAllProfiles());
            //       const twins = [];
            //       for (let i=0; i<people.length; i++) {
            //         if (people[i].twin_id_id === profile.twin_id_id) twins.push(people[i]);
            //       }
            //
            //       if (twins.length === 1) {
            //         // delete the twin entry and clear twin_id and twin_set
            //         // if there is only one twin left after deletion
            //
            //         /* Note: deletion of twin record is done within member deletion if
            //         ** only one twin is left, no need to manually do it
            //         */
            //         // await this.pedigreeWrapper.deleteTwinsEntry(profile);
            //
            //         twins[0].twin_id_id = null;
            //         twins[0].twin_id = null;
            //         twins[0].twin_set = null;
            //         twins[0].twin_type = null;
            //         this.pedigreeWrapper.props.getPedigreeData().setProfile(twins[0].rkey, twins[0]);
            //       }
            //     }
            //
            //     if (this.pedigreeWrapper.state.showSideBar) {
            //       this.pedigreeWrapper.handleSideBarClose();
            //     }
            //
            //     this.pedigreeWrapper.setState({
            //       openModalConfirmDelete: false,
            //       errorMessages: []
            //     });
            //
            //     // we don't have to redraw when deleting anymore because we already have a unique and constant id for the connector nodes
            //     // await this.pedigreeWrapper.saveLayoutData({})
            //     // await this.pedigreeWrapper.props.getPedigreeData().setSavedNodePositions('');
            //
            //     await this.pedigreeWrapper.reRenderPedigree();
            //
            //   } catch (error) {
            //     console.log(error)
            //     this.pedigreeWrapper.setState({ errorMessages: [error.message] });
            //   }
            // }

        }
    }

    /**
     * Validate if node can be deleted
     */
    validateDeleteOrFail(node, pedigreeData) {
        const profile = pedigreeData.getProfile(node.id);

        if (!profile) {
            return { canDelete: false, reason: 'Profile not found' };
        }

        // Check if node has children
        const children = pedigreeData.getChildren(profile.id);
        if (children && children.length > 0) {
            return { canDelete: false, reason: 'Cannot delete member with children' };
        }

        // Check if node is proband
        if (profile.is_proband) {
            return { canDelete: false, reason: 'Cannot delete proband' };
        }

        return { canDelete: true };
    }

    /**
     * Helper method to get child gender
     */
    static getChildGender(value) {
        switch (value) {
            case actions.SON:
            case actions.MALE_FETAS:
                return "m";
            case actions.DAUGHTER:
            case actions.FEMALE_FETAS:
                return "f";
            case actions.UNKNOWN:
            case actions.PREGNANCY_UNKNOWN:
            case actions.SAB:
                return null;
            default:
                break;
        }

        return null;
    }

    /**
     * Helper method to get sibling gender
     */
    static getSiblingGender(value) {
        switch (value) {
            case actions.SIBLING_SON:
            case actions.SIBLING_TWIN_BROTHER:
                return "m";
            case actions.SIBLING_DAUGHTER:
            case actions.SIBLING_TWIN_SISTER:
                return "f";
            case actions.SIBLING_UNKNOWN:
            case actions.SIBLING_TWIN_UNKNOWN:
                return null;
            default:
                break;
        }

        return null;
    }

    /**
     * Get spouse from profile data
     */
    static async getSpouse(profile) {
        let partners = null;
        try {
            partners = await familyApi.get_member_memberid_partners(profile.id);
        } catch (error) {
            throw error;
        }

        if (partners && Array.isArray(partners) && partners.length > 0 && partners.length === 1) {
            return partners[0]
        } else {
            throw new Error(`Multiple Partners - Please add children to the proper partner`);
        }
    }

    /**
     * Create member as child
     */
    static async createMemberAsChild(spouse, partner, child, side, should_send_notification) {
        try {
            return await helper_family_api.create_member_as_child(
                spouse,
                partner,
                child,
                side,
                should_send_notification
            );
        } catch (error) {
            console.error('Error creating child:', error);
        }
    }
}